import { ReactElement, useEffect, useState } from "react";
import { IEAFProduction, IParentSize } from "../../../types";
import TableContainer from "../../../components/tableContainer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from "@mui/material";
import StyledTableRow from "../../../components/styledTableRow";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckIcon from "@mui/icons-material/Check";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { formatDateTimeWithSeconds, saveBlobAs, toTons } from "../../../system/utils";
import { Alloy_Tolerance } from "./types";

interface IProps {
  productionData: IEAFProduction[];
  parentSize: IParentSize;
}

interface IIndicatorProps {
  odd?: true;
  element?: {
    weight?: number;
    suggestedValue?: number;
    setPointValue?: number;
  };
}

const valueWidth = 70;
const subHeaderTop = 37;
const rowHeight = 36;

const columnABase = {
  backgroundColor: "#6d7d85",
  top: subHeaderTop,
  padding: "4px"
};

const columnBBase = {
  backgroundColor: "#b0bec5",
  top: subHeaderTop,
  padding: "4px"
};

const columnA = {
  ...columnABase,
  width: valueWidth,
  minWidth: valueWidth,
};

const columnB = {
  ...columnBBase,
  width: valueWidth,
  minWidth: valueWidth,
};


const getElementCsv  = (element?: { weight?: number, suggestedValue?: number, setPointValue?: number, }): string => {
  const weight = element?.weight ?? 0;
  const suggestedValue = element?.suggestedValue ?? 0;
  const setPointValue = element?.setPointValue ?? 0;
  const diff = weight - suggestedValue;

  return `${suggestedValue.toFixed(2)};${setPointValue.toFixed(2)};${weight.toFixed(2)};${diff.toFixed(2)};`;
};

const Indicator = ({ element, odd }: IIndicatorProps): ReactElement => {
  const weight = element?.weight ?? 0;
  const suggestedValue = element?.suggestedValue ?? 0;
  const setPointValue = element?.setPointValue ?? 0;
  const diff = weight - suggestedValue;
  const style = {
    backgroundColor: odd ? "rgba(0, 0, 0, .1)" : undefined,
    padding: "4px",
    width: valueWidth,
    minWidth: valueWidth,
  };
  const diffStyle = {
    ...style,
  };

  let icon = <CheckIcon />;
  if (suggestedValue * Alloy_Tolerance < Math.abs(diff)) {
    diffStyle.backgroundColor = "red";
    icon = diff > 0 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />;
  }

  return (
    <>
      <TableCell align="right" style={style} >
        {suggestedValue.toFixed(0)}
      </TableCell>
      <TableCell align="right" style={style} >
        {setPointValue.toFixed(0)}
      </TableCell>
      <TableCell align="right" style={style} >
        {weight.toFixed(0)}
      </TableCell>
      <TableCell align="right" style={diffStyle}>
        {Math.abs(diff).toFixed(0)}
      </TableCell>
      <TableCell align="right" style={{ ...diffStyle,   width: 29,
        minWidth: 29, }} >
        {icon}
      </TableCell>
    </>
  );
};

const ProductionAlloys = ({ productionData, parentSize }: IProps): ReactElement => {
  const tableContainerHeight = parentSize.height - 88;
  const [ records, setRecords ] = useState<Record<string, any>[]>([]);
  const [ totals, setTotals ] = useState<Record<string, any>>({});
  const hasScroll = productionData.length * 38 > tableContainerHeight;

  useEffect(() => {
    const newData: Record<string, any>[] = [];
    const recordTotal: Record<string, any> = {};

    productionData.forEach(row => {
      if(!row.endDatetime) {
        return;
      }

      const record: Record<string, any> = {
        heatNumber: row.heatNumber,
        beginDatetime: formatDateTimeWithSeconds(row.beginDatetime),
        endDatetime: row.endDatetime ? formatDateTimeWithSeconds(row.endDatetime) : "",
        steel: row.steel.code,
        crew: row.crew.name,
        powerOn: row.powerON,
        powerOff: row.powerOFF,
        kwh: row.kwh,
        weight: row.weight
      };

      row.additions.forEach(a => {
        a.additionDetail.forEach(d => {
          const key = d.material.name;
          record[key] = record[key] ?? { weight: 0, suggestedValue: 0, setPointValue: 0 };
          record[key].weight += d.weight ?? 0;
          record[key].suggestedValue += d.suggestedValue ?? 0;
          record[key].setPointValue += d.setPointValue ?? 0;

          if (!recordTotal[key]) {
            recordTotal[key] = {
              weight: d.weight ?? 0,
              suggestedValue: d.suggestedValue ?? 0,
              setPointValue: d.setPointValue ?? 0
            };
          } else {
            recordTotal[key].weight += d.weight ?? 0;
            recordTotal[key].suggestedValue += d.suggestedValue ?? 0;
            recordTotal[key].setPointValue += d.setPointValue ?? 0;
          }
        });
      });

      newData.push(record);
    });

    setRecords(newData);
    setTotals(recordTotal);
  }, [productionData]);

  const onExportData = () => {
    let fileContent = "";
    fileContent += ";;;;;;;;;Spar;;;Ladle Graphite;;;Si Mn;;;Fe Si;;;Hi Cal;;;Dolo lime;;;SiC\n";
    fileContent += "Heat Number;Steel;Crew;Begin;End;Power On;Power Off;kwh;Weight;";
    fileContent += "SPEC;SP;REAL;DIFF;SPEC;SP;REAL;DIFF;SPEC;SP;REAL;DIFF;SPEC;SP;REAL;DIFF;SPEC;SP;REAL;DIFF;SPEC;SP;REAL;DIFF;SPEC;SP;REAL;DIFF\n";

    records.forEach((row: any) => {
      fileContent += `${row.heatNumber};${row.steel};${row.crew};${row.beginDatetime};${row.endDatetime};`;
      fileContent += `${row.powerOn};${row.powerOff};${row.kwh};${toTons(row.weight).toFixed(2)};`;

      fileContent += getElementCsv(row.Spar);
      fileContent += getElementCsv(row["Ladle Graphite"]);
      fileContent += getElementCsv(row["Si Mn"]);
      fileContent += getElementCsv(row["Fe Si"]);
      fileContent += getElementCsv(row["Hi Cal"]);
      fileContent += getElementCsv(row["Dolo lime"]);
      fileContent += getElementCsv(row.SiC);
      fileContent += "\n";
    });

    const file = new Blob([ fileContent ], { type: "text/csv" });

    saveBlobAs(URL.createObjectURL(file), "alloys.csv");
  };

  const spanRow = tableContainerHeight - rowHeight * records.length - subHeaderTop * 3;

  return (
    <>
      <TableContainer  className="flex-grow-1" style={{ maxHeight: tableContainerHeight, height: tableContainerHeight, overflowX: "auto" }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell style={{ backgroundColor: "#b0bec5" }} colSpan={3} />
              <TableCell style={{ backgroundColor: "#6d7d85" }} colSpan={5} align="center">Spar<sub className="ml-1">[ lb ]</sub></TableCell>
              <TableCell style={{ backgroundColor: "#b0bec5" }} colSpan={5} align="center">Ladle Graphite<sub className="ml-1">[ lb ]</sub></TableCell>
              <TableCell style={{ backgroundColor: "#6d7d85" }} colSpan={5} align="center">Si Mn<sub className="ml-1">[ lb ]</sub></TableCell>
              <TableCell style={{ backgroundColor: "#b0bec5" }} colSpan={5} align="center">Fe Si<sub className="ml-1">[ lb ]</sub></TableCell>
              <TableCell style={{ backgroundColor: "#6d7d85" }} colSpan={5} align="center">Hi Cal<sub className="ml-1">[ lb ]</sub></TableCell>
              <TableCell style={{ backgroundColor: "#b0bec5" }} colSpan={5} align="center">Dolo lime<sub className="ml-1">[ lb ]</sub></TableCell>
              <TableCell style={{ backgroundColor: "#6d7d85" }} colSpan={5} align="center">SiC<sub className="ml-1">[ lb ]</sub></TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ backgroundColor: "#b0bec5", top: 37, padding: 2  }}>Heat</TableCell>
              <TableCell style={{ backgroundColor: "#b0bec5", top: 37, minWidth: 105, maxWidth: 105, padding: 2 }}>Steel</TableCell>
              <TableCell style={{ backgroundColor: "#b0bec5", top: 37, minWidth: 45, maxWidth: 45, padding: 2  }}>Crew</TableCell>
              <TableCell style={columnA} align="center">SPEC</TableCell>
              <TableCell style={columnA} align="center">SP</TableCell>
              <TableCell style={columnA} align="center">REAL</TableCell>
              <TableCell style={columnA} align="center">DIFF</TableCell>
              <TableCell style={columnABase} className="p-1"/>
              <TableCell style={columnB} align="center">SPEC</TableCell>
              <TableCell style={columnB} align="center">SP</TableCell>
              <TableCell style={columnB} align="center">REAL</TableCell>
              <TableCell style={columnB} align="center">DIFF</TableCell>
              <TableCell style={columnBBase}/>
              <TableCell style={columnA} align="center">SPEC</TableCell>
              <TableCell style={columnA} align="center">SP</TableCell>
              <TableCell style={columnA} align="center">REAL</TableCell>
              <TableCell style={columnA} align="center">DIFF</TableCell>
              <TableCell style={columnABase}/>
              <TableCell style={columnB} align="center">SPEC</TableCell>
              <TableCell style={columnB} align="center">SP</TableCell>
              <TableCell style={columnB} align="center">REAL</TableCell>
              <TableCell style={columnB} align="center">DIFF</TableCell>
              <TableCell style={columnBBase}/>
              <TableCell style={columnA} align="center">SPEC</TableCell>
              <TableCell style={columnA} align="center">SP</TableCell>
              <TableCell style={columnA} align="center">REAL</TableCell>
              <TableCell style={columnA} align="center">DIFF</TableCell>
              <TableCell style={columnABase}/>
              <TableCell style={columnB} align="center">SPEC</TableCell>
              <TableCell style={columnB} align="center">SP</TableCell>
              <TableCell style={columnB} align="center">REAL</TableCell>
              <TableCell style={columnB} align="center">DIFF</TableCell>
              <TableCell style={columnBBase}/>
              <TableCell style={columnA} align="center">SPEC</TableCell>
              <TableCell style={columnA} align="center">SP</TableCell>
              <TableCell style={columnA} align="center">REAL</TableCell>
              <TableCell style={columnA} align="center">DIFF</TableCell>
              <TableCell style={columnABase}/>
            </TableRow>
          </TableHead>
          <TableBody>
            { records.map((row, index) => {

              return (
                <StyledTableRow key={`addition-${index}`}>
                  <TableCell style={{ padding: 4 }}>{row.heatNumber}</TableCell>
                  <TableCell style={{ padding: 4 }}>{row.steel}</TableCell>
                  <TableCell style={{ padding: 4 }}>{row.crew}</TableCell>
                  <Indicator element={row.SPAR} odd={true} />
                  <Indicator element={row["Ladle Graphite"]} />
                  <Indicator element={row["Si Mn"]} odd={true} />
                  <Indicator element={row["Fe Si"]} />
                  <Indicator element={row["Hi Cal"]} odd={true} />
                  <Indicator element={row["Dolo lime"]} />
                  <Indicator element={row.SIC} odd={true} />
                </StyledTableRow>
              );
            })}
            {spanRow > 0 && <TableRow style={{ height: spanRow }}/>}
            <TableRow>
              <TableCell style={{ padding: 4 }}>Total</TableCell>
              <TableCell style={{ padding: 4, minWidth: 50, maxWidth: 50, width: 50 }} />
              <TableCell style={{ padding: 4, minWidth: 45, maxWidth: 45, width: 45 }}/>
              <Indicator element={totals.SPAR} odd={true} />
              <Indicator element={totals["Ladle Graphite"]} />
              <Indicator element={totals["Si Mn"]} odd={true} />
              <Indicator element={totals["Fe Si"]} />
              <Indicator element={totals["Hi Cal"]} odd={true} />
              <Indicator element={totals["Dolo lime"]} />
              <Indicator element={totals.SIC} odd={true} />
              {hasScroll && <TableCell width={14} style={{ padding: 0 }} />  }
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Table stickyHeader={true} size={"small"}>
        <TableBody>

          <TableRow>
            <TableCell>
              <IconButton color="primary" onClick={onExportData} disabled={records.length === 0}>
                <Tooltip title={"Export data"}>
                  <FileDownloadIcon/>
                </Tooltip>
              </IconButton>
            </TableCell>
            <TableCell colSpan={28}/>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default ProductionAlloys;